<template>
  <el-dialog
    :modelValue="modelValue"
    @update:modelValue="$emit('update:modelValue', $event)"
    title="과거 견적 비교"
    width="950px"
    top="5vh"
  >
    <!-- 검색 영역 -->
    <div class="search-area">
      <el-form :inline="true" :model="searchForm" size="small">
        <el-form-item label="브랜드">
          <el-select v-model="searchForm.brand" clearable filterable placeholder="브랜드 선택" style="width: 150px;">
            <el-option
              v-for="brand in brands"
              :key="brand.id"
              :label="brand.name"
              :value="brand.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="스타일번호">
          <el-input v-model="searchForm.design_keyword" placeholder="스타일번호" style="width: 150px;" @keyup.enter="handleSearch" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch" :loading="searching">검색</el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- 검색 결과 (체크박스 선택) -->
    <div class="search-results" v-if="searchResults.length > 0">
      <el-table
        :data="searchResults"
        size="small"
        max-height="200"
        @selection-change="handleSelectionChange"
        ref="searchTable"
      >
        <el-table-column type="selection" width="40" :selectable="canSelect" />
        <el-table-column prop="quotation_number" label="견적번호" width="130" />
        <el-table-column prop="brand_name" label="브랜드" width="100" />
        <el-table-column prop="design_style_number" label="스타일" width="120" />
        <el-table-column label="발주수량" width="90" align="right">
          <template #default="{ row }">{{ row.order_quantity?.toLocaleString() }}</template>
        </el-table-column>
        <el-table-column label="견적가" width="110" align="right">
          <template #default="{ row }">{{ formatCurrency(row.quotation_price) }}</template>
        </el-table-column>
        <el-table-column label="상태" width="80">
          <template #default="{ row }">
            <el-tag size="small" :type="row.status === 'CONFIRMED' ? 'warning' : 'success'">
              {{ row.status_display }}
            </el-tag>
          </template>
        </el-table-column>
      </el-table>
      <div class="selection-info">
        <el-text type="info" size="small">최대 3건 선택 가능 ({{ selectedQuotations.length }}/3)</el-text>
      </div>
    </div>

    <!-- 비교 테이블 -->
    <div class="compare-section" v-if="selectedQuotations.length > 0">
      <el-divider>비교 (최대 3건)</el-divider>
      <div class="compare-table-wrapper">
        <table class="compare-table">
          <thead>
            <tr>
              <th class="label-col">항목</th>
              <th class="current-col">
                현재
                <div class="col-sub">{{ currentQuotation?.quotation_number }}</div>
              </th>
              <th v-for="sq in selectedQuotations" :key="sq.id" class="compare-col">
                {{ sq.quotation_number }}
                <div class="col-sub">{{ sq.brand_name }}</div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="label-col">발주수량</td>
              <td class="current-col num">{{ currentQuotation?.order_quantity?.toLocaleString() }}</td>
              <td v-for="sq in selectedQuotations" :key="sq.id" class="num">{{ sq.order_quantity?.toLocaleString() }}</td>
            </tr>
            <tr v-for="item in compareItems" :key="item.key" :class="{ 'highlight-row': item.highlight }">
              <td class="label-col">{{ item.label }}</td>
              <td class="current-col num">{{ formatCurrency(currentQuotation?.[item.key]) }}</td>
              <td v-for="sq in selectedQuotations" :key="sq.id" class="num" :class="getDiffClass(currentQuotation?.[item.key], sq[item.key])">
                {{ formatCurrency(sq[item.key]) }}
                <span v-if="getDiffPercent(currentQuotation?.[item.key], sq[item.key])" class="diff-badge">
                  {{ getDiffPercent(currentQuotation?.[item.key], sq[item.key]) }}
                </span>
              </td>
            </tr>
            <tr class="highlight-row">
              <td class="label-col">PCS단가</td>
              <td class="current-col num">{{ formatCurrency(currentQuotation?.effective_unit_price) }}</td>
              <td v-for="sq in selectedQuotations" :key="sq.id" class="num" :class="getDiffClass(currentQuotation?.effective_unit_price, sq.effective_unit_price)">
                {{ formatCurrency(sq.effective_unit_price) }}
                <span v-if="getDiffPercent(currentQuotation?.effective_unit_price, sq.effective_unit_price)" class="diff-badge">
                  {{ getDiffPercent(currentQuotation?.effective_unit_price, sq.effective_unit_price) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <el-empty v-if="searched && searchResults.length === 0" description="검색 결과가 없습니다." />

    <template #footer>
      <el-button @click="$emit('update:modelValue', false)">닫기</el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import axiosInstance from '@/api/axios'
import { useEstimateStore } from '@/stores/estimateStore'

const props = defineProps({
  modelValue: Boolean,
  currentQuotation: { type: Object, default: () => ({}) }
})

defineEmits(['update:modelValue'])

const store = useEstimateStore()
const brands = ref([])
const searchResults = ref([])
const selectedQuotations = ref([])
const searching = ref(false)
const searched = ref(false)
const searchTable = ref(null)

const searchForm = reactive({
  brand: null,
  design_keyword: ''
})

const compareItems = [
  { key: 'self_raw_material_cost', label: '자체원자재비' },
  { key: 'brand_raw_material_cost', label: '브랜드원자재비' },
  { key: 'self_accessory_cost', label: '자체부자재비' },
  { key: 'brand_accessory_cost', label: '브랜드부자재비' },
  { key: 'self_material_subtotal', label: '소계(자체)', highlight: true },
  { key: 'cmt_cost', label: '임가공비' },
  { key: 'logistics_cost', label: '물류비' },
  { key: 'customs_cost', label: '관세' },
  { key: 'manufacturing_cost', label: '제조원가', highlight: true },
  { key: 'margin_amount', label: '마진' },
  { key: 'quotation_price', label: '견적가', highlight: true },
]

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '-'
  return Number(amount).toLocaleString() + '원'
}

const canSelect = (row) => {
  if (selectedQuotations.value.length >= 3 && !selectedQuotations.value.find(s => s.id === row.id)) {
    return false
  }
  // 현재 견적은 선택 불가
  if (props.currentQuotation && row.id === props.currentQuotation.id) {
    return false
  }
  return true
}

const handleSelectionChange = (selection) => {
  selectedQuotations.value = selection.slice(0, 3)
}

const handleSearch = async () => {
  searching.value = true
  searched.value = true
  try {
    const params = {}
    if (searchForm.brand) params.brand = searchForm.brand
    if (searchForm.design_keyword) params.design_keyword = searchForm.design_keyword
    const results = await store.searchQuotations(params)
    searchResults.value = results || []
  } catch (error) {
    console.error('Search failed:', error)
  } finally {
    searching.value = false
  }
}

// 차이 계산
const getDiffClass = (currentVal, compareVal) => {
  const curr = Number(currentVal) || 0
  const comp = Number(compareVal) || 0
  if (curr === 0 || comp === 0) return ''
  if (comp > curr * 1.05) return 'diff-higher'
  if (comp < curr * 0.95) return 'diff-lower'
  return ''
}

const getDiffPercent = (currentVal, compareVal) => {
  const curr = Number(currentVal) || 0
  const comp = Number(compareVal) || 0
  if (curr === 0) return ''
  const diff = ((comp - curr) / curr * 100).toFixed(1)
  if (Math.abs(diff) < 0.1) return ''
  return diff > 0 ? `+${diff}%` : `${diff}%`
}

const fetchBrands = async () => {
  try {
    const response = await axiosInstance.get('brands/')
    brands.value = response.data.results || response.data
  } catch (error) {
    console.error('Failed to fetch brands:', error)
  }
}

onMounted(() => {
  fetchBrands()
})
</script>

<style scoped>
.search-area {
  margin-bottom: 15px;
}

.search-results {
  margin-bottom: 10px;
}

.selection-info {
  margin-top: 5px;
  text-align: right;
}

.compare-section {
  margin-top: 10px;
}

.compare-table-wrapper {
  overflow-x: auto;
}

.compare-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.compare-table th,
.compare-table td {
  border: 1px solid #ebeef5;
  padding: 8px 12px;
}

.compare-table thead th {
  background-color: #f5f7fa;
  font-weight: 600;
  text-align: center;
}

.label-col {
  width: 130px;
  font-weight: 500;
  background-color: #fafafa;
}

.current-col {
  background-color: #ecf5ff;
  font-weight: 600;
}

.compare-col {
  min-width: 150px;
}

.num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.col-sub {
  font-size: 11px;
  color: #909399;
  font-weight: normal;
}

.highlight-row td {
  background-color: #fdf6ec;
  font-weight: 600;
}

.highlight-row .current-col {
  background-color: #d9ecff;
}

.diff-higher {
  color: #f56c6c;
}

.diff-lower {
  color: #67c23a;
}

.diff-badge {
  display: inline-block;
  font-size: 10px;
  margin-left: 4px;
  padding: 1px 4px;
  border-radius: 3px;
  background-color: #f0f0f0;
}

.diff-higher .diff-badge {
  background-color: #fef0f0;
  color: #f56c6c;
}

.diff-lower .diff-badge {
  background-color: #f0f9eb;
  color: #67c23a;
}
</style>
